﻿import { TweenMax, TimelineMax, gsap} from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { sitecore, MediaQuery } from '../../Foundation';

export class HeroScroll{

    /**
     * @param {HTMLElement} element
     */
    constructor(element){
        let _this = this;
        this.element = element;

        this._refs = {
            el: element,
            slides: Array.from(element.querySelectorAll('.hero-scroll__slide-item')),
            productImage: element.querySelector('.hero-scroll__image'),
            overlay: element.querySelector('.hero-scroll__overlay'),
            context: element.querySelector('.hero-scroll__heading'),
            nudge: element.querySelector('.hero-scroll__tip')
        };

        // # Pagemode
        if (sitecore.isInPageEditor) { 
            return;
        }

        // # Visitormode
        if (gsap.ScrollTrigger === undefined) { 
            gsap.registerPlugin(ScrollTrigger);
        }
     
        // Load background-image
        this._setBackgroundImage(); 

        // Introductory Animation 
        this.introAnimation();

        this.mainScrollAnimationRef = this.mainScrollAnimation();
        this.swipeExploreScrollAnimationRef = this.swipeExploreScrollAnimation();

        // On lazy loading, refresh
        window.addEventListener('image.loaded', () => {
            ScrollTrigger.refresh();
        });

        window.addEventListener('change.mq', (event) => {
            const mq = event.detail;
            if(mq.currSize.breakpoint === "xs" || mq.currSize.breakpoint === "lg") {
                this._setBackgroundImage();
            }

            // Reflow recalculation
            const scrollAnims = [_this.mainScrollAnimationRef, _this.swipeExploreScrollAnimationRef];
            _this.cleanAnimations(scrollAnims);
            this.introAnimation();
            _this.mainScrollAnimationRef = this.mainScrollAnimation();
            _this.swipeExploreScrollAnimationRef = this.swipeExploreScrollAnimation();
        });
    }

    introAnimation(){
        // Calculate timeline   
        let oMainTimeline = new TimelineMax();
        oMainTimeline.addLabel('introStart');
        
        // Intro-slide animation 
        oMainTimeline = this.heroImageAnimationIntro(oMainTimeline);
        oMainTimeline = this.heroHeadingAnimationIntro(oMainTimeline);
        oMainTimeline = this.heroScrollTipAnimationIntro(oMainTimeline);
        return oMainTimeline;
    }

    mainScrollAnimation(){
        const headElement = document.querySelector('#header');
        const offset = headElement ? Math.ceil(headElement.getBoundingClientRect().height) + 1 : 0;

        // Main Scene construction
        let st = ScrollTrigger.create({
            trigger: this._refs.el,
            start: "top-=" + offset + "px",
            end: '+=' + (MediaQuery.above('md') ? 1000 : 500),
            pin: true,
            scrub: 0.3,
            animation: this.fn() 
        }); 

        return st;
    }

    swipeExploreScrollAnimation(){
        // Dissapear "swipe to explore" scene
        let st = ScrollTrigger.create({
            trigger: this._refs.el,
            start: '+=' + (MediaQuery.above('md') ? 1500 : 500),
            end: '+=' + (MediaQuery.above('md') ? 600 : 450),
            scrub: 0.3,
            animation: this.scrollTipAnimationDisappear()
        });
        return st;
    }

    cleanAnimations(scrollTriggerAnims){
        for (var i = 0; i< scrollTriggerAnims.length; i++){
            scrollTriggerAnims[i].disable();
            scrollTriggerAnims[i].animation.seek(0).invalidate();
            this.clearProps(scrollTriggerAnims[i].animation);
            scrollTriggerAnims[i].animation.kill();
            scrollTriggerAnims[i].kill();
            scrollTriggerAnims[i] = undefined;
        }
    }

    fn () {
        let oTimeline = new TimelineMax();
        oTimeline.addLabel("startTimeLabel");
        oTimeline = this.heroOverlayAnimation(oTimeline);
        oTimeline = this.heroImageAnimation(oTimeline);
        oTimeline = this.heroHeadingAnimation(oTimeline);
        oTimeline = this.heroSlidesAnimation(oTimeline);

        return oTimeline; 
    }

    clearProps(scene) {
        var targets = scene.getChildren();
        for (var i = 0; i < targets.length; i++) {
            TweenMax.set(targets[i]._targets, {
                clearProps: "transform,opacity,width,height,maxWidth,maxHeight,padding,paddingTop,paddingRight,paddingBottom,paddingLeft,margin,marginTop,marginRight,marginBottom,marginLeft,position,top,right,bottom,left,transformOrigin,lineHeight"
            });
        }
    }
  
    /** 
     * Get path for BackgroundMediaItem
     * @returns {String} - Path to media item
     */
    _getBackgroundImage () {
        let media = '';

        if(MediaQuery.below('lg')) {
            if(typeof this._refs.el.dataset.backgroundXs != 'undefined') {
                media = this._refs.el.dataset.backgroundXs;
            } else if (typeof this._refs.el.dataset.backgroundLg != 'undefined') {
                media = this._refs.el.dataset.backgroundLg;
            }
        } else if (MediaQuery.above('lg')) {
            if (typeof this._refs.el.dataset.backgroundLg != 'undefined') {
                media = this._refs.el.dataset.backgroundLg;
            }
        }
 
        return media;
    }

    /**
     * Set path for BackgroundMediaItem
     */
    _setBackgroundImage () {
        const strMediaItem = this._getBackgroundImage();
        if(strMediaItem != '') this._refs.el.style.backgroundImage = "url(" + strMediaItem + ")";
    }

    /**
     * Creating animation for productImage
     * @param {Object} oTimeline - Timeline for GSAP
     * @returns {Object} - oTimeline 
     */
    heroImageAnimationIntro(oTimeline) {
        const 
            animationFrom = {
                opacity: 1, 
                scale: 1.1, 
                ease: "power1.out"
            },
            animationTo = {
                duration: 1.5, 
                opacity: 1, 
                scale: 1, 
                ease: "power1.out"
            };
            
        oTimeline.fromTo(this._refs.productImage, animationFrom, animationTo, "introStart");
        return oTimeline;
    }
    
    /**
     * Create animation-timeline for first-slide
     * @param {Object} oTimeline 
     * @returns {Object} - oTimeline
     */
    heroHeadingAnimationIntro (oTimeline) {
        const 
            animationFrom = {
                duration: 1.3, 
                y: '35px', 
                ease: "power1.inOut",
            },
            animationTo = {
                duration: 1.3, 
                y: 0,
                opacity: 1,
                ease: "power1.inOut"
            },
            elements = {
                h3: this._refs.context.querySelector('h3'),
                h1: this._refs.context.querySelector('h1'),
                p: this._refs.context.querySelector('p')
            };
        
        if(elements.h3 != null) {
            oTimeline.fromTo(elements.h3, animationFrom, animationTo, "introStart");
        }

        if(elements.h1 != null) {
            oTimeline.fromTo(elements.h1, animationFrom, animationTo, "-=1.2");
        }

        if(elements.p != null) {
            oTimeline.fromTo(elements.p, animationFrom, animationTo, "-=1.2");
        }

        return oTimeline;
    }

    /**
     * Animation for nudge-area
     * @param {Object} oTimeline - Animation of timeline
     * @returns {Object} - oTimeline 
     */
    heroScrollTipAnimationIntro (oTimeline) {
        const animation = {
            duration: 1.5, 
            y: '50px', 
            opacity: 0, 
            ease: "power1.inOut"
        };
        
        oTimeline.from(this._refs.nudge, animation, "introStart");
        return oTimeline;
    }

    /**
     * 
     * @param {*} oTimeline 
     */
    heroImageAnimation (oTimeline) {
        const 
            animationDuration = MediaQuery.above('md') ? 4 : 0.2,
            mobileAnimationHide = {
                duration: animationDuration, 
                opacity: 0, 
                scale: 1,
                ease: 'power1.out'
            },
            mobileAnimationScale = {
                duration: ((animationDuration) * this._refs.slides.length) + 0.2, 
                scale: 1.3, 
                opacity: 1, 
                ease: 'power1.out'}
            ,
            desktopAnimation = {
                duration: animationDuration, 
                left: '50%'
            };

        if (MediaQuery.below("md")){
            oTimeline.set(this._refs.productImage, {scale: 1}, "startTimeLabel");
            oTimeline.fromTo(this._refs.productImage, {scale: 1}, mobileAnimationHide, 'startTimeLabel');
            oTimeline.set(this._refs.productImage, {top: '50%', marginTop: '-150px', scale: 1}, ">");
            oTimeline.to(this._refs.productImage, mobileAnimationScale, ">");
        } else {
            oTimeline.set(this._refs.productImage, {scale: 1}, 'startTimeLabel');
            oTimeline.to(this._refs.productImage, desktopAnimation, 'startTimeLabel');
            oTimeline.to(this._refs.productImage, {duration: ((animationDuration * 1.2) * this._refs.slides.length), scale: .7, ease: 'power1.out'}, "-=3");
        }

        return oTimeline;
    }

    heroOverlayAnimation(oTimeline){
        const animationDuration = MediaQuery.above("md")? 3 : 0.5;
        oTimeline.to(this._refs.overlay, {duration: animationDuration, opacity: 0.9, ease:"expo.out"}, 'startTimeLabel');
        return oTimeline;
    }

    heroHeadingAnimation(oTimeline){
        if (MediaQuery.below("md")){
            oTimeline.to(this._refs.context, {duration: 0.2, opacity: 0, y: '-30%', ease: "power1.out"}, 'startTimeLabel');
        } else {
            oTimeline.to(this._refs.context, {duration: 1.5, opacity: 0, top: '25%', ease: "power1.in"}, 'startTimeLabel');
        }
        return oTimeline;
    }

    heroSlidesAnimation(oTimeline){
        const 
            slideInDuration = MediaQuery.above('md') ? 1.5 : 0.2, // Slide In Animation duration
            moveUpDuration = MediaQuery.above('md') ? 1.3 : 0.2, // Move up Animation duration
            slideOutDuration = MediaQuery.above('md') ? 3 : 0.1, // Slide Out Animation duration
            slideAnimationDuration = slideInDuration + moveUpDuration + slideOutDuration, // Total Duration of Full Single Slide animation 
            lineHeightValue = MediaQuery.above('md') ? '70px' : '42px',
            // Text Slide In
            textSlideInAnimation = {
                duration: slideInDuration,
                opacity: 1, 
                marginTop: 0,
                lineHeight: lineHeightValue,
                ease: "none"
            },
            // Text Move Up 
            textMoveUpAnimation = {
                duration: moveUpDuration, 
                marginTop: '-80px',
                ease: "none"
            }, 
            // Text Slide Out
            textSlideOutAnimation = {
                duration: slideOutDuration, 
                opacity: 0, 
                marginTop: '-150px',
                ease: "power1.out"
            };

        oTimeline.addLabel('slidesStart');
        
        const animationOverlap = MediaQuery.above("md")? 4 : 0.35;

        //Animate each slide
        this._refs.slides.forEach((slide, i) => {

            let slideOffsetStart = (slideAnimationDuration * this._refs.slides.length) - (slideAnimationDuration * (i+1)) + animationOverlap;
            oTimeline.to(slide, 
                {
                    keyframes: [
                        textSlideInAnimation, textMoveUpAnimation, textSlideOutAnimation]
                }, 
                'slidesStart -=' + slideOffsetStart
            );
        });
        return oTimeline;
    }

    scrollTipAnimationDisappear(){
        const 
            animationDuration = 5, 
            animationFrom = {
                opacity: 1,
            },
            animationTo = {
                duration: animationDuration,
                opacity: 0,
                ease: "power1.inOut"
            };

        let oTimeline = new TimelineMax();
        
        oTimeline.fromTo(this._refs.nudge, animationFrom, animationTo);
        return oTimeline;
    }
}